<script setup lang="ts">
import { formatDateTimeLong, formatDateTimeRelative, formatNumber } from '@metapic-apps/shared'
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { formatMoneyForAdmin } from '@/lib/formatters/money.formatter'
import { convertToPercentage, isFutureDate } from '@/lib/helpers'
import { ReturnAdStatusEnum } from '@/lib/types/return-ad.types'
import { type ReturnAdListItemType } from '@/lib/types/return-ads.types'
import {
  BoxComponent,
  FlexComponent,
  ImageComponent,
  ProgressComponent,
  TagComponent,
  TooltipComponent,
  TypographyComponent,
} from '@/ui-library'

const props = defineProps<{ item: ReturnAdListItemType }>()

const router = useRouter()
const returnAdsStatusMap = {
  [ReturnAdStatusEnum.Active]: 'Active',
  [ReturnAdStatusEnum.Paused]: 'Paused',
  [ReturnAdStatusEnum.Scheduled]: 'Scheduled',
  [ReturnAdStatusEnum.Ended]: 'Ended',
  [ReturnAdStatusEnum.Deleted]: 'Deleted',
}

const shouldShowDate = computed(() => Boolean(isFutureDate(props.item.startsAt || '') || isFutureDate(props.item.endsAt || '')))

const dateLabel = (dateFrom: string | undefined, dateUntil: string | undefined) => {
  if (dateFrom && isFutureDate(dateFrom)) {
    return `Starts ${formatDateTimeRelative(dateFrom)}`
  }
  if (dateUntil && isFutureDate(dateUntil)) {
    return `Ends ${formatDateTimeRelative(dateUntil)}`
  }
}

const dateFull = (dateFrom: string | undefined, dateUntil: string | undefined) => {
  if (dateFrom && isFutureDate(dateFrom)) {
    return formatDateTimeLong(dateFrom)
  }
  if (dateUntil && isFutureDate(dateUntil)) {
    return formatDateTimeLong(dateUntil)
  }
}

const handleClick = (id: number) => {
  return router.push(`/return-ads/${id}`)
}
</script>
<template>
  <BoxComponent :pt="8" :pb="8" :pr="8" :pl="24" class="return-list-item__container" @click="() => handleClick(item.id)">
    <FlexComponent align="center" justify="space-between" :gap="8">
      <BoxComponent w="30%">
        <FlexComponent :gap="8" align="center">
          <ImageComponent
            v-if="item.affiliateLink?.advertiser.logo"
            :image="item.affiliateLink.advertiser.logo"
            :alt="item.affiliateLink.advertiser.name"
            :width="64"
            :height="32"
            object-fit="contain"
            resize-type="fit"
          />
          <TypographyComponent variant="label-medium">{{ item.affiliateLink?.advertiser.name }}</TypographyComponent>
        </FlexComponent>
        <FlexComponent :gap="8" align="center" wrap="nowrap">
          <TagComponent v-if="item.status" :extra-class="`return-list-item__tag return-list-item__tag-${item.status?.toLowerCase()}`">
            {{ returnAdsStatusMap[item.status] }}
          </TagComponent>
          <TypographyComponent variant="title-large" white-space="nowrap" text-truncate>{{ item.internalName }}</TypographyComponent>
        </FlexComponent>
        <BoxComponent :mt="6">
          <TypographyComponent variant="body-small" color="var(--secondary-48)">
            <FlexComponent align="center">
              <span class="return-list-item__footer-item"> ID: {{ item.id }} </span>
              <template v-if="shouldShowDate && item.status !== ReturnAdStatusEnum.Ended && item.status !== ReturnAdStatusEnum.Deleted">
                <TooltipComponent>
                  <template #default>
                    <span class="return-list-item__footer-item">
                      {{ dateLabel(item.startsAt, item.endsAt) }}
                    </span>
                  </template>
                  <template #content>
                    <TypographyComponent variant="body-small" white-space="nowrap">
                      {{ dateFull(item.startsAt, item.endsAt) }}
                    </TypographyComponent>
                  </template>
                </TooltipComponent>
              </template>
            </FlexComponent>
          </TypographyComponent>
        </BoxComponent>
      </BoxComponent>

      <BoxComponent w="70%">
        <FlexComponent :gap="48" align="center" flex="1" justify="end">
          <FlexComponent :gap="8" align="center" flex="1" justify="end">
            <BoxComponent :pl="16" :pr="16" w="16.6%" min-w="120px">
              <TypographyComponent variant="label-medium" text-align="center">Views</TypographyComponent>
              <BoxComponent :mt="4">
                <TooltipComponent
                  placement="bottom"
                  effect="light"
                  :show-arrow="false"
                  :disabled="item.stats.currentViews < 1000 && !item.viewLimit"
                >
                  <TypographyComponent v-if="!item.viewLimit" variant="title-large" text-align="center">
                    {{ formatNumber(item.stats.currentViews, 'compact') }}
                  </TypographyComponent>
                  <ProgressComponent
                    v-else
                    :content="`${formatNumber(item.stats.currentViews, 'compact')}/${formatNumber(item.viewLimit, 'compact')}`"
                    :percentage="Math.ceil((item.stats.currentViews / item.viewLimit) * 100)"
                  />
                  <template #content>
                    {{
                      item.viewLimit
                        ? `${formatNumber(item.stats.currentViews)}/${formatNumber(item.viewLimit)}`
                        : formatNumber(item.stats.currentViews)
                    }}
                  </template>
                </TooltipComponent>
              </BoxComponent>
            </BoxComponent>
            <BoxComponent :pl="16" :pr="16" w="16.6%" min-w="120px">
              <TypographyComponent variant="label-medium" text-align="center">Clicks</TypographyComponent>
              <BoxComponent :mt="4">
                <TooltipComponent
                  placement="bottom"
                  effect="light"
                  :show-arrow="false"
                  :disabled="item.stats.currentClicks < 1000 && !item.clickLimit"
                >
                  <TypographyComponent v-if="!item.clickLimit" variant="title-large" text-align="center">
                    {{ formatNumber(item.stats.currentClicks, 'compact') }}
                  </TypographyComponent>
                  <TypographyComponent v-else variant="title-large" text-align="center">
                    <ProgressComponent
                      :content="`${formatNumber(item.stats.currentClicks, 'compact')}/${formatNumber(item.clickLimit, 'compact')}`"
                      :percentage="Math.ceil((item.stats.currentClicks / item.clickLimit) * 100)"
                    />
                  </TypographyComponent>
                  <template #content>
                    {{
                      item.clickLimit
                        ? `${formatNumber(item.stats.currentClicks)}/${formatNumber(item.clickLimit)}`
                        : formatNumber(item.stats.currentClicks)
                    }}
                  </template>
                </TooltipComponent>
              </BoxComponent>
            </BoxComponent>
            <BoxComponent :pl="16" :pr="16" w="16.6%">
              <TooltipComponent placement="bottom" effect="light" :show-arrow="false">
                <div>
                  <TypographyComponent variant="label-medium" text-align="center">CTR</TypographyComponent>
                  <TypographyComponent variant="title-large" text-align="center">
                    {{ convertToPercentage(item.stats.clickThroughRate) }}
                  </TypographyComponent>
                </div>
                <template #content>
                  <TypographyComponent variant="title-medium">Click-through rate (CTR)</TypographyComponent>
                  <TypographyComponent variant="body-medium"
                    >CTR is the ratio of clicks on a return ad to its total views.
                  </TypographyComponent>
                </template>
              </TooltipComponent>
            </BoxComponent>
            <BoxComponent :pl="16" :pr="16" w="16.6%">
              <TypographyComponent variant="label-medium" text-align="center">Orders</TypographyComponent>
              <TooltipComponent placement="bottom" effect="light" :show-arrow="false" :disabled="item.stats.orderCount < 1000">
                <TypographyComponent variant="title-large" text-align="center">
                  {{ formatNumber(item.stats.orderCount, 'compact') }}
                </TypographyComponent>
                <template #content>
                  {{ formatNumber(item.stats.orderCount, 'standard') }}
                </template>
              </TooltipComponent>
            </BoxComponent>
            <BoxComponent :pl="16" :pr="16" w="16.6%">
              <TooltipComponent placement="bottom" effect="light" :show-arrow="false">
                <div>
                  <TypographyComponent variant="label-medium" text-align="center">CR</TypographyComponent>
                  <TypographyComponent variant="title-large" text-align="center">
                    {{ convertToPercentage(item.stats.conversionRate || 0) }}
                  </TypographyComponent>
                </div>
                <template #content>
                  <TypographyComponent variant="title-medium">Conversion rate (CR)</TypographyComponent>
                  <TypographyComponent variant="body-medium"
                    >Conversion rate is the number of orders divided by clicks.
                  </TypographyComponent>
                </template>
              </TooltipComponent>
            </BoxComponent>
            <BoxComponent :pl="16" :pr="16" w="16.6%">
              <TypographyComponent variant="label-medium" white-space="nowrap" text-align="center">Order value</TypographyComponent>
              <TooltipComponent placement="bottom" effect="light" :show-arrow="false" :disabled="item.stats.orderValue.amount < 1000">
                <TypographyComponent variant="title-large" white-space="nowrap" text-align="center">
                  {{
                    formatMoneyForAdmin(
                      { amount: item.stats.orderValue.amount, currency: item.stats.orderValue.currency },
                      { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 1 },
                    )
                  }}
                </TypographyComponent>
                <template #content>
                  {{
                    formatMoneyForAdmin(
                      { amount: item.stats.orderValue.amount, currency: item.stats.orderValue.currency },
                      { allowZero: true },
                    )
                  }}
                </template>
              </TooltipComponent>
            </BoxComponent>
            <BoxComponent :pl="16" :pr="16" w="16.6%">
              <TypographyComponent variant="label-medium" white-space="nowrap" text-align="center">Earnings</TypographyComponent>
              <TooltipComponent placement="bottom" effect="light" :show-arrow="false" :disabled="item.stats.earnings.amount < 1000">
                <TypographyComponent variant="title-large" white-space="nowrap" text-align="center">
                  {{
                    formatMoneyForAdmin(
                      { amount: item.stats.earnings.amount, currency: item.stats.earnings.currency },
                      { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 1 },
                    )
                  }}
                </TypographyComponent>
                <template #content>
                  {{
                    formatMoneyForAdmin({ amount: item.stats.earnings.amount, currency: item.stats.earnings.currency }, { allowZero: true })
                  }}
                </template>
              </TooltipComponent>
            </BoxComponent>
          </FlexComponent>
          <BoxComponent :w="68" class="return-list-item__image">
            <ImageComponent
              v-if="item.image"
              :image="item.image"
              :width="68"
              :height="120"
              skip-height
              alt="Campaign image"
              class="return-list-item__campaign-image"
            />
          </BoxComponent>
        </FlexComponent>
      </BoxComponent>
    </FlexComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.return-list-item__container {
  cursor: pointer;
  position: relative;
  border-radius: 16px;

  border: 1px solid var(--button-hover);
  background: var(--inverted);

  &:hover {
    background: var(--secondary-16);

    .return-list-item__actions {
      display: flex;
    }
  }

  & > div {
    min-height: 120px;
  }
}

.return-list-item__actions {
  display: none;
  position: absolute;
  z-index: 1;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  border-radius: 16px;
  background: var(--secondary-16);
}
.return-list-item__campaign-image {
  border-radius: 8px;
  object-fit: contain;
  display: block;
}
.return-list-item__footer-item {
  white-space: nowrap;

  &:not(:last-child):after {
    content: '|';
    margin: 0 8px;
  }
}
.return-list-item__limits-view {
  color: var(--inverted);
  border-radius: 48px;
  padding: 0 8px;
  background: rgba(34, 34, 34, 0.32);
  position: relative;

  &:before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: var(--success);
    padding: 0 8px;
    border-radius: 48px;
    top: 0;
    left: 0;
  }
}

:deep(.return-list-item__tag) {
  height: 32px;
  color: var(--inverted);

  &.return-list-item__tag-scheduled {
    background: #17a2b8;
  }
  &.return-list-item__tag-active {
    background: var(--success);
  }
  &.return-list-item__tag-paused {
    background: #f3ba00;
  }
  &.return-list-item__tag-ended {
    background: var(--alert);
  }
}

.return-list-item__image {
  flex: 0 0 68px;
}
</style>
